import { config } from "@/config";
import { useAuthHeaders } from "@/hooks/use-auth-headers";
import { MessageResponse } from "@/types/chat";
import { DiagramComponent } from "@/types/diagram";
import { ChatMode } from "@/components/elements/chat-mode-selector";
import { useMutation, UseMutateFunction } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { escapeChatQuery } from "@/lib/sanitize-chat-query";
import { useCallback, useRef } from "react";

type SendChatRequestProps = {
  query: string;
  uuid: string;
  agent: string;
  currentState?: DiagramComponent[];
  file?: File;
  gitUrl?: string;
  gitToken?: string;
   gitBranch?: string;
   chatMode?: ChatMode;
   isDeepAnalysis?: boolean;
   threadId?: string;
};

async function sendChatRequest(
  {
    query,
    uuid,
    currentState,
    agent,
    file,
    gitUrl,
    gitToken,
    gitBranch,
    chatMode,
    isDeepAnalysis,
    threadId,
    token,
  }: SendChatRequestProps & { token: string },
  signal?: AbortSignal
): Promise<MessageResponse> {
  const formData = new FormData();
  if (file) {
    // On Windows, .md and .txt files may have an incorrect MIME type (e.g.
    // "application/octet-stream") because Windows lacks a default registry
    // entry for those extensions. The backend uses the MIME type to detect the
    // file kind and set `uploadedType`; without the correct type the
    // "Generate Functional Metrics" button is never shown.
    // Re-create the File with the proper MIME type when needed.
    const EXTENSION_MIME_MAP: Record<string, string> = {
      ".md": "text/markdown",
      ".markdown": "text/markdown",
      ".txt": "text/plain",
      ".csv": "text/csv",
      ".json": "application/json",
      ".html": "text/html",
      ".htm": "text/html",
      ".docx":
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ".pptx":
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      ".xlsx":
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    };
    const ext = Object.keys(EXTENSION_MIME_MAP).find((e) =>
      file.name.toLowerCase().endsWith(e)
    );
    const needsMimeOverride =
      ext &&
      (file.type === "" || file.type === "application/octet-stream");
    const fileToUpload = needsMimeOverride
      ? new File([file], file.name, { type: EXTENSION_MIME_MAP[ext!] })
      : file;
    formData.append("file", fileToUpload);
  }
  if (currentState)
    formData.append("currentState", JSON.stringify(currentState));

  if (gitToken) formData.append("gitToken", gitToken.trim());
  if (gitUrl) formData.append("gitUrl", gitUrl.trim());
   if (gitBranch) formData.append("git_branch", gitBranch.trim());
   if(chatMode) formData.append("chatMode", chatMode);
   if(isDeepAnalysis) formData.append("isDeepAnalysis", "true");

  // Protect the request payload itself. The backend repeats this check for
  // non-WebUI clients before forwarding or persisting the query.
  formData.append("query", escapeChatQuery(query));
  formData.append("uuid", uuid);
  formData.append("agent", agent);
  if (threadId) formData.append("threadId", threadId);

  const response = await apiFetch(`${config.isometricApiUrl}/chat/v2`, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    signal,
  });

  if (!response.ok) {
    const error: any = new Error("Failed to send the message.");
    error.status = response.status;
    throw error;
  }

  const res: MessageResponse = await response.json(); // Parse the JSON response

  return res;
}
interface UseSaveMessageServiceResult {
  mutate: UseMutateFunction<MessageResponse, Error, SendChatRequestProps>;
  abort: () => void;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  data: MessageResponse | undefined;
  reset: () => void;
}

export default function useSaveMessageService(
  onSettled?: (res: MessageResponse | undefined, error: Error | null) => void
): UseSaveMessageServiceResult {
  const { getAuthHeaders } = useAuthHeaders();
  const abortControllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: (params: SendChatRequestProps) => {
      abortControllerRef.current = new AbortController();
      const headers = getAuthHeaders();
      const token = headers.Authorization.replace("Bearer ", "");
      return sendChatRequest(
        { ...params, token },
        abortControllerRef.current.signal
      );
    },
    onSettled: (data, error) => {
      if (onSettled) {
        onSettled(data ?? undefined, error instanceof Error ? error : null);
      }
      abortControllerRef.current = null;
    },
  });

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    mutate: mutation.mutate,
    abort,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
}
